import { twMerge } from "tailwind-merge";
import { Icon } from "@/components/Icon";

export interface Step {
  id: string;
  title: string;
  description?: string;
}

export interface FormStepperProps {
  steps: Step[];
  currentStepId: string;
  onStepClick?: (id: string) => void;
  className?: string;
}

export function FormStepper({ steps, currentStepId, onStepClick, className }: FormStepperProps) {
  const currentIndex = steps.findIndex((s) => s.id === currentStepId);

  return (
    <nav aria-label="Progress" className={twMerge("w-full", className)}>
      <div className="relative mb-12">
        {/* Steps container with known structure */}
        <ol className="relative flex items-start justify-between">
          {steps.map((step, index) => {
            const isActive = step.id === currentStepId;
            const isCompleted = index < currentIndex;
            const isClickable = onStepClick && (isCompleted || isActive);

            return (
              <li
                key={step.id}
                className="relative flex flex-col items-center"
                style={{ flex: `1 1 ${100 / steps.length}%` }}
              >
                {/* Step Circle */}
                <button
                  type="button"
                  disabled={!isClickable}
                  onClick={() => isClickable && onStepClick?.(step.id)}
                  className={twMerge(
                    "relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors bg-background-body",
                    isCompleted &&
                      "border-action-default bg-action-default text-text-onPrimary hover:bg-action-default/90",
                    isActive && "border-action-default bg-background-body text-action-default",
                    !isCompleted && !isActive && "border-border-default bg-background-body text-text-muted",
                    isClickable && "cursor-pointer",
                    !isClickable && "cursor-default"
                  )}
                  aria-current={isActive ? "step" : undefined}
                >
                  {isCompleted ? (
                    <Icon name="Check" size={20} weight="bold" />
                  ) : (
                    <span className="text-sm font-semibold">{index + 1}</span>
                  )}
                </button>

                {/* Connecting line to next step - rendered inside the step item */}
                {index < steps.length - 1 && (
                  <div
                    className="absolute top-5 h-0.5 transition-colors"
                    style={{
                      left: "50%",
                      width: "100%",
                      marginLeft: "20px",
                      backgroundColor:
                        index < currentIndex ? "var(--color-action-default)" : "var(--color-border-default)",
                    }}
                  />
                )}

                {/* Step Label */}
                <div className="mt-3 flex flex-col items-center text-center w-32">
                  <span
                    className={twMerge(
                      "text-sm font-medium",
                      isActive && "text-text-default",
                      isCompleted && "text-text-body",
                      !isActive && !isCompleted && "text-text-muted"
                    )}
                  >
                    {step.title}
                  </span>
                  {step.description && <span className="mt-1 text-xs text-text-caption">{step.description}</span>}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}
